import { commonUtils } from '@/helpers/commonUtils'
import styles from './PptPage7.module.less'
import CoreChart, { useChartConfig } from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/CoreChart/CoreChart'
import LegendDot from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/LegendDot/LegendDot'
import VideoAnalysisChartCover from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysisChart/components/VideoAnalysisChartCover/VideoAnalysisChartCover'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PptPage, { PageHeader } from '../PptPage/PptPage'
import { SliderModels } from '@/types/slider'
import html2canvas from 'html2canvas'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
import cls from 'classnames'

interface Props {
  project: ProjectModels.ProjectDetail
  no: number
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
}

function PptPage7({ project, no, onSliderReady }: Props) {
  const { t } = useTranslation()
  const chartConfig = useChartConfig(project.detailMetrics)
  const chartGrid = chartConfig.config.grid as any
  const isPortraitLayout = isPortraitByProjectInfo(project)

  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()

  const titleRef = useRef<HTMLSpanElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const legendRef = useRef<HTMLDivElement>(null)
  const [chartImage, setChartImage] = useState<string>()
  const [legendImage, setLegendImage] = useState<string>()

  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }
  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }

  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null)
  const [coverRendered, setCoverRendered] = useState(false)

  useEffect(() => {
    if (!titleRef.current || !chartRef.current || !chartImage || !legendRef.current || !legendImage) {
      return
    }
    const titleRect = titleRef.current.getBoundingClientRect()
    const chartRect = chartRef.current.getBoundingClientRect()
    const legendRect = legendRef.current.getBoundingClientRect()

    const elements: SliderModels.PPTElement[] = [
      {
        type: 'text',
        content: titleRef.current.outerHTML,
        x: titleRect.x,
        y: titleRect.y - 70,
        width: titleRect.width,
        height: titleRect.height,
      },
      {
        type: 'image',
        src: chartImage,
        x: chartRect.x,
        y: chartRect.y,
        width: chartRect.width,
        height: chartRect.height,
      },
      {
        type: 'image',
        src: legendImage,
        x: legendRect.x,
        y: legendRect.y,
        width: legendRect.width,
        height: legendRect.height,
      },
    ]
    setElements(elements)
  }, [chartImage, legendImage])

  useEffect(() => {
    if (!pageRect || !basicSlider || pageHeaderElements.length === 0 || elements.length === 0) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements])

  useEffect(() => {
    setTimeout(() => {
      if (!chartRef.current || !coverRendered || !chartInstance) {
        return
      }
      html2canvas(chartRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      }).then((canvas) => {
        const base64Image = canvas.toDataURL('image/png')
        setChartImage(base64Image)
      })
    }, 1000)
  }, [chartInstance, coverRendered])

  useEffect(() => {
    setTimeout(() => {
      if (!legendRef.current || !coverRendered) {
        return
      }
      html2canvas(legendRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      }).then((canvas) => {
        setLegendImage(canvas.toDataURL('image/png'))
      })
    }, 1000)
  }, [coverRendered])

  return (
    <PptPage
      bgSrc={commonUtils.getImagePath('PPTDownload/bg-6.jpg')}
      no={no}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPage7}>
        <PageHeader
          project={project}
          text={'02.' + t('dashboard.projectDetailPage.primaryAnalysis')}
          onRender={onPageHeaderRender}
        />
        <div className={styles.content}>
          <div className={styles.contentTitle}>
            <span ref={titleRef} style={{ fontSize: 64, fontWeight: 'bold', color: '#2A2C3F' }}>
              {t('dashboard.projectDetailPage.videoView.subjectiveResponseCurve')}
            </span>
          </div>
          <div
            className={styles.chartCapture}
            style={{ height: isPortraitLayout ? 635 : 670, overflow: 'hidden' }}
            ref={chartRef}
          >
            <div className={cls(styles.chart, isPortraitLayout && styles.chartPortrait)}>
              <CoreChart
                className={styles.pdfChart}
                detailMetrics={project.detailMetrics}
                options={{ height: '92%', dotSize: 10, labelVisible: true }}
                onChartReady={(instance) => {
                  /**
                   * 这里延时稍微长一点，确保转化为 base64 时，echarts 已经渲染完成。
                   * NOTE: 之前设置的 1000ms 不太够，导致转化为图片时最后几个点显示不够完全。
                  */
                  // setTimeout(() => {
                  setChartInstance(instance as unknown as echarts.ECharts)
                  // }, 2000)
                }}
              />
              <div
                className={styles.cover}
                style={{
                  width: `calc(100% - ${chartGrid.left + chartGrid.right}px)`,
                  marginLeft: chartGrid.left,
                }}
              >
                <VideoAnalysisChartCover
                  chartInstance={chartInstance}
                  project={project}
                  orientation={isPortraitLayout ? 'portrait' : 'landscape'}
                  thumbnailMode={isPortraitLayout ? 'portrait-ppt' : 'default'}
                  onRendered={() => {
                    /**
                     * 因为这里组件依赖 chartInstance 的渲染，所以需要等待 chartInstance 渲染完成。
                     * 延时比 chartInstance 渲染完成的时间要长，确保转化图片时，echarts 已经渲染完成。
                    */
                    setTimeout(() => {
                      setCoverRendered(true)
                    },)
                  }}
                />
              </div>
            </div>
          </div>
          <div className={styles.legendRow} ref={legendRef}>
            <LegendDot layout='centered-row' />
          </div>
        </div>
      </div>
    </PptPage>
  )
}

export default PptPage7
